import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clearStoredAccessKey,
  fetchFluxReferenceTransferBuildInfo,
  generateFluxReferenceTransfer,
} from '../utils/fluxReferenceTransferApi';
import '../../flux-prompt-sample/components/FluxPromptSampleAccessGate.css';
import '../../flux-prompt-sample/pages/FluxPromptSamplePage.css';

const ASPECT_OPTIONS = [
  { id: 'makeup', label: 'Makeup' },
  { id: 'hair', label: 'Hair + flowers (replaces guest hair)' },
  { id: 'outfit', label: 'Outfit' },
  { id: 'jewelry', label: 'Jewelry (full set: tikka, nose ring, necklaces…)' },
  { id: 'background', label: 'Background' },
];

const ALL_ASPECT_IDS = ASPECT_OPTIONS.map((o) => o.id);

const TRANSFER_SCOPE_OPTIONS = [
  {
    id: 'complete_keep_face',
    label: 'Complete transfer (keep face)',
    hint:
      'Default. Reference is another model — copy look elements only. Guest face stays image 1. Headshot framing kept unless match reference pose is on.',
  },
  {
    id: 'custom',
    label: 'Pick elements',
    hint: 'Choose makeup, hair, outfit, and other items to transfer.',
  },
];

const PRESERVE_FACE_OPTIONS = [
  {
    id: 'strict',
    label: 'Strict',
    hint: 'Keep guest face, features, and skin tone exactly (recommended).',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    hint: 'Same person; allow minor lighting or color harmony on the face.',
  },
  {
    id: 'off',
    label: 'Relaxed',
    hint: 'Prioritize matching reference look; less identity lock on the face.',
  },
];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

export default function FluxReferenceTransferPage() {
  const guestFileRef = useRef(null);
  const refFileRef = useRef(null);
  const [guestImage, setGuestImage] = useState('');
  const [referenceImage, setReferenceImage] = useState('');
  const [aspects, setAspects] = useState(() => ['makeup', 'hair', 'outfit']);
  const [preserveFaceMode, setPreserveFaceMode] = useState('strict');
  const [transferScope, setTransferScope] = useState('complete_keep_face');
  const [transferPose, setTransferPose] = useState(false);
  const [prompt, setPrompt] = useState('');

  const isCompleteTransfer = transferScope === 'complete_keep_face';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resultImage, setResultImage] = useState('');
  const [meta, setMeta] = useState(null);
  const [rev, setRev] = useState('');

  useEffect(() => {
    fetchFluxReferenceTransferBuildInfo()
      .then((d) => setRev(d?.rev || ''))
      .catch(() => {});
  }, []);

  const onPickGuest = async (file) => {
    if (!file) return;
    setError('');
    try {
      setGuestImage(await readFileAsDataUrl(file));
      setResultImage('');
      setMeta(null);
    } catch (e) {
      setError(e.message || 'Could not read file');
    }
  };

  const onPickReference = async (file) => {
    if (!file) return;
    setError('');
    try {
      setReferenceImage(await readFileAsDataUrl(file));
      setResultImage('');
      setMeta(null);
    } catch (e) {
      setError(e.message || 'Could not read file');
    }
  };

  const toggleAspect = (id) => {
    setAspects((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const onTransferScopeChange = (id) => {
    setTransferScope(id);
    if (id === 'complete_keep_face') {
      if (!aspects.length) setAspects(ALL_ASPECT_IDS);
      setPreserveFaceMode('strict');
    }
  };

  const onGenerate = useCallback(async () => {
    if (!guestImage) {
      setError('Choose a guest image first.');
      return;
    }
    if (!referenceImage) {
      setError('Choose a reference image (style source).');
      return;
    }
    const aspectsForRun = isCompleteTransfer && !aspects.length ? ALL_ASPECT_IDS : aspects;
    if (!aspectsForRun.length) {
      setError('Select at least one element to transfer.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await generateFluxReferenceTransfer({
        image: guestImage,
        referenceImage,
        aspects: aspectsForRun,
        preserveFaceMode: isCompleteTransfer ? 'strict' : preserveFaceMode,
        transferScope,
        transferPose,
        prompt: prompt.trim(),
      });
      setResultImage(data.image || '');
      setMeta(data.meta || null);
    } catch (e) {
      setError(e.message || 'Generation failed');
      setResultImage('');
      setMeta(null);
      if (e.code === 'SAMPLE_ACCESS_DENIED') clearStoredAccessKey();
    } finally {
      setBusy(false);
    }
  }, [
    guestImage,
    referenceImage,
    aspects,
    preserveFaceMode,
    transferScope,
    transferPose,
    isCompleteTransfer,
    prompt,
  ]);

  const onSignOut = () => {
    clearStoredAccessKey();
    window.location.reload();
  };

  return (
    <div className="fps-page">
      {busy ? <div className="fps-busy" role="status">Running FLUX…</div> : null}
      <header className="fps-header">
        <div>
          <h1>FLUX reference transfer</h1>
          <p className="fps-sub">
            Dual-image i2i: image 1 keeps face and identity; image 2 donates makeup, hair, outfit, and scene
            only (never a face swap).{' '}
            <Link to="/flux-prompt-sample" className="fps-link-btn">Single-image prompt lab</Link>
          </p>
        </div>
        <div className="fps-header-actions">
          <span className="fps-tag">{rev ? `rev ${rev}` : 'preview'}</span>
          <button type="button" className="fps-link-btn" onClick={onSignOut}>
            Change key
          </button>
        </div>
      </header>

      <div className="fps-grid">
        <section className="fps-panel">
          <h2>Input</h2>
          <label className="fps-gate-label" htmlFor="frt-guest-file">Guest (identity)</label>
          <input
            id="frt-guest-file"
            ref={guestFileRef}
            type="file"
            accept="image/*"
            className="fps-file"
            onChange={(e) => onPickGuest(e.target.files?.[0])}
          />
          <label className="fps-gate-label" htmlFor="frt-ref-file">Reference (style source)</label>
          <input
            id="frt-ref-file"
            ref={refFileRef}
            type="file"
            accept="image/*"
            className="fps-file"
            onChange={(e) => onPickReference(e.target.files?.[0])}
          />
          <p className="fps-meta">
            Image 1 = guest identity · Image 2 = reference look · Strict i2i (not creative remix)
          </p>
          <fieldset className="fps-aspects fps-preserve-face">
            <legend>Transfer mode</legend>
            {TRANSFER_SCOPE_OPTIONS.map((opt) => (
              <label key={opt.id} className="fps-preserve-face-option">
                <input
                  type="radio"
                  name="transfer-scope"
                  checked={transferScope === opt.id}
                  onChange={() => onTransferScopeChange(opt.id)}
                />
                <span className="fps-preserve-face-label">{opt.label}</span>
                <span className="fps-preserve-face-hint">{opt.hint}</span>
              </label>
            ))}
          </fieldset>
          <label className="fps-aspect-chip fps-pose-chip">
            <input
              type="checkbox"
              checked={transferPose}
              onChange={(e) => setTransferPose(e.target.checked)}
            />
            Match reference pose (body stance from reference; face still from guest)
          </label>
          <fieldset className="fps-aspects">
            <legend>Elements to transfer (exact match from reference)</legend>
            {isCompleteTransfer ? (
              <p className="fps-meta">Complete mode locks face; check only what to copy from reference.</p>
            ) : null}
            {ASPECT_OPTIONS.map((opt) => (
              <label key={opt.id} className="fps-aspect-chip">
                <input
                  type="checkbox"
                  checked={aspects.includes(opt.id)}
                  onChange={() => toggleAspect(opt.id)}
                />
                {opt.label}
              </label>
            ))}
          </fieldset>
          <fieldset className="fps-aspects fps-preserve-face" disabled={isCompleteTransfer}>
            <legend>Preserve guest face</legend>
            {isCompleteTransfer ? (
              <p className="fps-meta">Locked to Strict for complete transfer.</p>
            ) : null}
            {PRESERVE_FACE_OPTIONS.map((opt) => (
              <label key={opt.id} className="fps-preserve-face-option">
                <input
                  type="radio"
                  name="preserve-face-mode"
                  checked={preserveFaceMode === opt.id}
                  onChange={() => setPreserveFaceMode(opt.id)}
                  disabled={isCompleteTransfer}
                />
                <span className="fps-preserve-face-label">{opt.label}</span>
                <span className="fps-preserve-face-hint">{opt.hint}</span>
              </label>
            ))}
          </fieldset>
          <textarea
            className="fps-prompt"
            rows={4}
            placeholder="Optional (e.g. softer lipstick). Leave empty to match reference exactly."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <button type="button" className="fps-btn" disabled={busy} onClick={onGenerate}>
            Transfer
          </button>
          {error ? <p className="fps-error" role="alert">{error}</p> : null}
        </section>

        <section className="fps-panel fps-previews">
          <h2>Preview</h2>
          <div className="fps-pair fps-pair--triple">
            <figure>
              <figcaption>Guest</figcaption>
              {guestImage ? (
                <img src={guestImage} alt="Guest preview" />
              ) : (
                <div className="fps-placeholder">No image</div>
              )}
            </figure>
            <figure>
              <figcaption>Reference</figcaption>
              {referenceImage ? (
                <img src={referenceImage} alt="Reference preview" />
              ) : (
                <div className="fps-placeholder">No image</div>
              )}
            </figure>
            <figure>
              <figcaption>Result</figcaption>
              {resultImage ? (
                <img src={resultImage} alt="Transfer result" />
              ) : (
                <div className="fps-placeholder">Run transfer</div>
              )}
            </figure>
          </div>
          {meta?.timing_ms?.pipe != null ? (
            <p className="fps-meta">pipe {meta.timing_ms.pipe} ms · storage none</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}
